import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { paymentApi, type PixPayment, type PaymentStatus } from '../api/payment';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { CrownIcon } from '../components/ui/icons';
import { useToast } from '../components/ui/Toast';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from '../contexts/AuthContext';
import { HOUSEHOLD_QUERY_KEY } from '../hooks/useHousehold';

const BENEFITS = [
  'Compartilhe a lista com toda a família',
  'Múltiplas listas de compras',
  'Histórico completo de preços',
  'Sugestoes inteligentes de compra',
];

const FALLBACK_PRICE = 'R$ 15,00/ano';

function formatPrice(priceCents: number): string {
  return `R$ ${(priceCents / 100).toFixed(2).replace('.', ',')}/ano`;
}

type Step = 'intro' | 'qrcode' | 'polling' | 'success';

export function PaymentScreen() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const { email } = useAuthContext();

  const [step, setStep] = useState<Step>('intro');
  const [payment, setPayment] = useState<PixPayment | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [priceLabel, setPriceLabel] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  useEffect(() => {
    paymentApi.getCheckoutConfig()
      .then((config) => setPriceLabel(formatPrice(config.priceCents)))
      .catch(() => setPriceLabel(FALLBACK_PRICE));
    return () => stopPolling();
  }, []);

  const handleCreatePix = async () => {
    setIsCreating(true);
    try {
      const p = await paymentApi.createPix(email);
      setPayment(p);
      setStep('qrcode');
    } catch {
      addToast('Erro ao gerar cobrança. Tente novamente.', 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!payment?.pixCopyPaste) return;
    try {
      await navigator.clipboard.writeText(payment.pixCopyPaste);
      addToast('Código Pix copiado!', 'success');
    } catch {
      addToast('Nao foi possivel copiar. Copie manualmente.', 'error');
    }
  };

  const handleCheckStatus = () => {
    if (!payment) return;
    setStep('polling');

    pollingRef.current = setInterval(async () => {
      try {
        // ADR 024 — the manual check queries the Mercado Pago API and can
        // activate the premium without depending solely on the webhook.
        const status = await paymentApi.checkPayment(payment.paymentId);
        setPaymentStatus(status);
        if (status.status === 'approved') {
          stopPolling();
          setStep('success');
          queryClient.invalidateQueries({ queryKey: HOUSEHOLD_QUERY_KEY });
          addToast('Pagamento confirmado! Bem-vindo ao Premium!', 'success');
        } else if (status.status === 'rejected' || status.status === 'cancelled') {
          stopPolling();
          addToast('Pagamento nao aprovado. Tente novamente.', 'error');
          setStep('qrcode');
        }
      } catch {
        // silent, keep polling
      }
    }, 5000);
  };

  const handleStopPolling = () => {
    stopPolling();
    setStep('qrcode');
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 pb-[calc(4rem+env(safe-area-inset-bottom)+1.5rem)]">
      <PageHeader title="Upgrade Premium" showBack />

      <div className="flex-1 px-4 py-5 space-y-5">

        {step === 'intro' && (
          <>
            <div className="bg-gradient-to-br from-brand-500 to-brand-600 rounded-3xl p-6 text-white">
              <p className="text-2xl font-bold">HomeStock Premium</p>
              <p className="text-brand-100 mt-1">Tudo que você precisa para organizar as compras da família</p>
              <p className="text-3xl font-bold mt-4">
                {priceLabel === null
                  ? <span className="opacity-50">...</span>
                  : priceLabel}
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-3">
              <p className="font-bold text-gray-900">O que esta incluido:</p>
              {BENEFITS.map((b) => (
                <div key={b} className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-brand-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="#059669" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-700">{b}</p>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="#1D4ED8">
                    <path d="M7 5H3v16h18V5h-4V3H7v2zm0 2h10v12H7V7z"/>
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Pagamento via Pix</p>
                  <p className="text-xs text-gray-500">Aprovação instantânea</p>
                </div>
              </div>
              <Button
                variant="primary"
                fullWidth
                size="lg"
                onClick={handleCreatePix}
                loading={isCreating}
                title="Gerar cobrança Pix"
                aria-label="Gerar cobrança Pix"
              >
                <CrownIcon size={18} />
              </Button>
            </div>
          </>
        )}

        {step === 'qrcode' && payment && (
          <>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex flex-col items-center gap-4">
              <p className="font-bold text-gray-900 text-lg text-center">Pague via Pix</p>
              <p className="text-3xl font-bold text-brand-600 text-center">
                R$ {payment.amount.toFixed(2).replace('.', ',')}
              </p>

              {payment.qrCode.startsWith('data:') || payment.qrCode.startsWith('http') ? (
                <img
                  src={payment.qrCode}
                  alt="QR Code Pix"
                  className="w-48 h-48 object-contain border border-gray-200 rounded-xl"
                />
              ) : (
                <div className="w-48 h-48 bg-gray-100 rounded-xl flex items-center justify-center p-3">
                  <p className="text-xs text-gray-500 text-center break-all font-mono">
                    {payment.qrCode.slice(0, 80)}...
                  </p>
                </div>
              )}

              <Button variant="secondary" fullWidth onClick={handleCopy}>
                Copiar código Pix
              </Button>

              <a
                href={payment.ticketUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl flex items-center justify-center text-sm transition-colors touch-manipulation"
              >
                Pagar no Mercado Pago
              </a>
            </div>

            <Button
              variant="ghost"
              fullWidth
              onClick={handleCheckStatus}
            >
              Ja paguei, verificar status
            </Button>
          </>
        )}

        {step === 'polling' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 flex flex-col items-center gap-5">
            <div className="animate-spin w-12 h-12 border-4 border-brand-500 border-t-transparent rounded-full" />
            <p className="font-semibold text-gray-900 text-center">Verificando pagamento...</p>
            <p className="text-sm text-gray-500 text-center">
              Aguarde enquanto confirmamos seu pagamento.
            </p>
            <Button variant="ghost" onClick={handleStopPolling}>
              Voltar
            </Button>
          </div>
        )}

        {step === 'success' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 flex flex-col items-center gap-5">
            <div className="w-20 h-20 bg-brand-100 rounded-full flex items-center justify-center">
              <svg width="40" height="40" fill="none" viewBox="0 0 24 24" stroke="#059669" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-2xl font-bold text-gray-900 text-center">Premium ativado!</p>
            <p className="text-sm text-gray-500 text-center">
              Obrigado! Agora você tem acesso completo ao HomeStock.
            </p>
            <Button variant="primary" fullWidth onClick={() => navigate('/settings')}>
              Ir para configurações
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
